import React, { useMemo, useEffect } from 'react';
import { Autocomplete, Box, Chip, FormLabel, Stack, TextField, Typography } from '@mui/material';
import moment from 'moment';

const CHIP_COLOR = '#1976d2';

const selectableChipSx = (selected, color = CHIP_COLOR) => ({
    bgcolor: selected ? color : 'transparent',
    color: selected ? '#fff' : color,
    border: `1px solid ${color}`,
    fontWeight: 500,
    cursor: 'pointer',
    '&:hover': { bgcolor: selected ? color : `${color}1a` },
});

const isMissingProjectDates = (proj) => !proj.start_date || !proj.end_date
    || moment(proj.pro_starts_on).isSameOrBefore('1900-01-01')
    || moment(proj.pro_ends_on).isSameOrBefore('1900-01-01');

const BasicDetsCard = (props) => {
    const taskDescriptionIsMandatory = useMemo(() => {
        if (props.user && props.user.comp_settings) {
            const setting = props.user.comp_settings.find(setting => setting.feature_id === 74);
            if (setting) {
                return setting.feature_value === 'true';
            }
        }
        return true;
    }, [props.user]);

    let unexpiredProjList = props.projList.filter((proj) =>
        Date.parse(proj.end_date) > Date.now() || (props.isSupport && isMissingProjectDates(proj))
    );

    if (props.customerPage && props.cus_id) {
        unexpiredProjList = unexpiredProjList.filter((proj) => proj.cus_id === props.cus_id);
    }

    const projList = unexpiredProjList.map((proj) => ({ value: proj.pid, title: proj.pname, cus_id: proj.cus_id }));

    useEffect(() => {
        if (!props.edit && projList.length === 1 && !props.formVals.tProject) {
            props.handleProjLoc(projList[0].value, 'tProject');
            props.handleProjChange(projList[0].cus_id, projList[0].value);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projList.length, props.edit, props.formVals.tProject]);

    const taskType = props.taskType.map((task) => ({ value: task, title: task }));

    useEffect(() => {
        if (!props.edit && taskType.length > 0 && !props.formVals.tType) {
            props.handleBasicDetsChange({ target: { id: 'tType', value: taskType[0].value } });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [taskType.length, props.edit, props.formVals.tType]);

    const filteredTasks = props.taskList.filter((task) =>
        +task.tas_pro_id === +props.formVals.tProject && task.tas_is_active === 0
    );
    const taskList = props.edit
        ? filteredTasks.map((task) => ({ value: task.tas_id, title: task.tas_title }))
        : [{ value: 'No Pre Req', title: 'No Pre Req' }, ...filteredTasks.map((task) => ({ value: task.id, title: task.task_name }))];

    const errTitle = props.submitAttempted && !props.formVals.tTitle;
    const errDesc = props.submitAttempted && taskDescriptionIsMandatory && !props.formVals.tDesc;
    const errProject = props.submitAttempted && !props.formVals.tProject;
    const errType = props.submitAttempted && !props.formVals.tType;

    return (
        <Stack spacing={2}>
            <TextField
                fullWidth
                label="Task Title"
                value={props.formVals.tTitle}
                onChange={(e) => props.handleBasicDetsChange(e)}
                inputProps={{ maxLength: 50 }}
                variant="outlined"
                size="small"
                id="tTitle"
                required
                error={errTitle}
                helperText={errTitle ? 'Task title is required' : ''}
            />

            <TextField
                fullWidth
                label={taskDescriptionIsMandatory ? 'Task Description' : 'Task Description (Optional)'}
                value={props.formVals.tDesc}
                onChange={(e) => props.handleBasicDetsChange(e)}
                variant="outlined"
                size="small"
                id="tDesc"
                required={taskDescriptionIsMandatory}
                inputProps={{ maxLength: 1000 }}
                multiline
                minRows={2}
                error={errDesc}
                helperText={errDesc ? 'Task description is required' : ''}
            />

            <Autocomplete
                id="tProject"
                fullWidth
                value={projList.find((pro) => +pro.value === +props.formVals.tProject) || null}
                size="small"
                onChange={(e, newValue) => {
                    if (!newValue) {
                        props.handleProjLoc('', 'tProject');
                        props.handleProjChange('', '');
                        return;
                    }
                    let value = projList.find((proj) => proj.title === newValue.title);
                    if (!!value) {
                        props.handleProjLoc(value.value, 'tProject');
                        props.handleProjChange(value.cus_id, value.value)
                    }
                    return;
                }}
                options={projList}
                getOptionLabel={(option) => option.title !== undefined ? option.title : option}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                renderOption={(props, option) => <li {...props} key={option.value}>{option.title}</li>}
                renderInput={(params) => <TextField {...params} label="Project" required variant="outlined" error={errProject || !!props.noEmployeeError} helperText={errProject ? 'Project is required' : (props.noEmployeeError || '')} />}
            />

            <Autocomplete
                id="tPreReq"
                fullWidth
                value={taskList.find((tas) => tas.value === props.formVals.tPreReq) || null}
                size="small"
                onChange={(e, newValue) => {
                    let value = taskList.find((tas) => tas.title === newValue.title)
                    if (!!value) {
                        props.handleProjLoc(value.value, 'tPreReq')
                    }
                    return
                }}
                options={taskList}
                getOptionLabel={(option) => option.title}
                renderInput={(params) => <TextField {...params} label="Task Prerequisite (Optional)" variant="outlined" />}
            />

            <Box>
                <FormLabel component="legend" required error={errType} sx={{ fontSize: '0.85rem', mb: 0.75, display: 'block' }}>
                    Type of Task
                </FormLabel>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    {taskType.length === 0 ? (
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                            No task types available
                        </Typography>
                    ) : (
                        taskType.map((opt) => {
                            const selected = props.formVals.tType === opt.value;
                            return (
                                <Chip
                                    key={opt.value}
                                    label={opt.title}
                                    onClick={() => { if (!selected) props.handleBasicDetsChange({ target: { id: 'tType', value: opt.value } }); }}
                                    sx={selectableChipSx(selected)}
                                />
                            );
                        })
                    )}
                </Box>
                {errType && (
                    <Typography variant="caption" sx={{ color: 'error.main', mt: 0.5, display: 'block' }}>
                        Task type is required
                    </Typography>
                )}
            </Box>

        </Stack>
    )
};

export default BasicDetsCard;
